'use client'

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/features/auth'
import { markNotificationsReadByEntityAction } from '../actions/notification.actions'
import { reconcileClearedIds } from '../hooks/use-mark-read'
import {
  ReadReceiptBuffer,
  type ReadReceipt,
  type ReceiptEntityType,
} from './read-receipt-buffer'

interface ReadReceiptContextValue {
  /** Queue a subject as "seen". Deduped + session-suppressed + batched by the
   *  service; flushes to `mark-read-by-entity` and reconciles cleared ids. */
  enqueue: (id: string, entityType: ReceiptEntityType) => void
}

const ReadReceiptContext = createContext<ReadReceiptContextValue | null>(null)

export function ReadReceiptProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient()
  const { user } = useAuth()

  const [buffer] = useState(
    () =>
      new ReadReceiptBuffer({
        flush: async (receipts: ReadReceipt[]) => {
          const res = await markNotificationsReadByEntityAction(
            receipts.map((r) => ({ id: r.id, entity_type: r.entityType }))
          )
          // null signals failure → the buffer requeues the batch.
          return res.success ? res.data : null
        },
        onCleared: (ids) => reconcileClearedIds(qc, ids),
      })
  )

  useEffect(() => {
    const onHidden = () => {
      if (document.visibilityState === 'hidden') void buffer.flush()
    }
    const onPageHide = () => void buffer.flush()
    document.addEventListener('visibilitychange', onHidden)
    window.addEventListener('pagehide', onPageHide)
    return () => {
      document.removeEventListener('visibilitychange', onHidden)
      window.removeEventListener('pagehide', onPageHide)
    }
  }, [buffer])

  const userId = user?.id ?? null
  const lastUserId = useRef(userId)
  useEffect(() => {
    if (lastUserId.current !== userId) {
      lastUserId.current = userId
      buffer.reset()
    }
  }, [userId, buffer])

  // Stop the periodic timer on teardown.
  useEffect(() => () => buffer.dispose(), [buffer])

  const value = useMemo<ReadReceiptContextValue>(
    () => ({ enqueue: (id, entityType) => buffer.enqueue(id, entityType) }),
    [buffer]
  )

  return (
    <ReadReceiptContext.Provider value={value}>
      {children}
    </ReadReceiptContext.Provider>
  )
}

/** Access the read-receipt enqueue. Must be used within a `ReadReceiptProvider`. */
export function useReadReceipts(): ReadReceiptContextValue {
  const ctx = useContext(ReadReceiptContext)
  if (ctx === null) {
    throw new Error(
      'useReadReceipts must be used within a ReadReceiptProvider'
    )
  }
  return ctx
}
